import React, {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Text,
  Textarea,
  Tooltip,
  VStack,
  Stack,
  Wrap,
  WrapItem,
  Switch,
  Spinner,
  Center,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon, DeleteIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { FiMic, FiVolume2 } from "react-icons/fi";
import TabCacheButton from "../../common/TabCacheButton";
import { hitBeApi } from "../../../api/api";
import { getCookieToken } from "../../../utils/utils";
import {
  getSpeechRecognitionClass,
  getPreferredSttLang,
  startBrowserVoiceDictation,
} from "../../../utils/browserSpeechRecognition";
import { speakBrowserAssistantReply } from "../../../utils/browserSpeechSpeak";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";

const OrgTree3DScene = React.lazy(() =>
  import("./OrgTree3DScene").then((m) => ({ default: m.OrgTree3DScene }))
);

const MotionBox = motion(Box);

const STORAGE_KEY = "manage360_org_tree_v1";

/** Keep in sync with manage360.me-server/services/orgTreeService.js limits. */
const ORG_TREE_MAX_TITLE = 240;
const ORG_TREE_MAX_FOCUS = 2000;
const ORG_TREE_MAX_TODO_TEXT = 600;

const KINDS = {
  org: { label: "Org", colorScheme: "purple" },
  team: { label: "Team", colorScheme: "blue" },
  person: { label: "Person", colorScheme: "teal" },
};

/** Work-tracker items on a node (separate from checklist todos). */
const PROJECT_STATUS_OPTIONS = [
  { value: "backlog", label: "Backlog" },
  { value: "active", label: "Active" },
  { value: "done", label: "Done" },
];

function emptyChildNode(overrides = {}) {
  return {
    id: newId(),
    title: "Untitled",
    kind: "team",
    focus: "",
    collapsed: false,
    children: [],
    projects: [],
    todos: [],
    people: [],
    managerMemberId: null,
    ...overrides,
  };
}

function normRosterEmail(s) {
  return s == null ? "" : String(s).trim().toLowerCase().slice(0, 200);
}

function normRosterPhone(s) {
  const d = s == null ? "" : String(s).replace(/\D/g, "");
  return d.slice(0, 20);
}

function rosterPhonesConflict(a, b) {
  const da = a == null ? "" : String(a).replace(/\D/g, "");
  const db = b == null ? "" : String(b).replace(/\D/g, "");
  if (!da || !db) return false;
  if (da === db) return true;
  if (da.length >= 10 && db.length >= 10 && da.slice(-10) === db.slice(-10)) return true;
  return false;
}

function looksLikeEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
}

function normalizePeopleAndManager(node) {
  const people = Array.isArray(node.people)
    ? node.people.map((p) => ({
        id: typeof p?.id === "string" && p.id ? p.id : newId(),
        name: p?.name != null ? String(p.name).slice(0, 120) : "Unnamed",
        role: p?.role != null ? String(p.role).slice(0, 80) : "",
        email: normRosterEmail(p?.email),
        phoneNumber: normRosterPhone(p?.phoneNumber ?? p?.phone),
      }))
    : [];
  let managerMemberId =
    node.managerMemberId != null && node.managerMemberId !== ""
      ? String(node.managerMemberId)
      : null;
  if (managerMemberId && !people.some((p) => p.id === managerMemberId)) {
    managerMemberId = null;
  }
  return { people, managerMemberId };
}

function normalizeTree(root) {
  if (!root || typeof root !== "object") return root;
  const rawProjects = Array.isArray(root.projects) ? root.projects : [];
  const projects = rawProjects.map((p) => {
    const name =
      p.name != null
        ? String(p.name)
        : p.title != null
          ? String(p.title)
          : "Untitled project";
    const status = PROJECT_STATUS_OPTIONS.some((o) => o.value === p.status)
      ? p.status
      : "backlog";
    return { id: p.id || newId(), name: name.slice(0, 200), status };
  });
  const { people, managerMemberId } = normalizePeopleAndManager(root);
  const todos = Array.isArray(root.todos)
    ? root.todos.map((t) => {
        let assignee = t.assigneeMemberId;
        if (assignee === "manager") {
          if (!managerMemberId) assignee = null;
        } else if (assignee && !people.some((p) => p.id === assignee)) {
          assignee = null;
        }
        return {
          id: t.id || newId(),
          text:
            t.text != null
              ? String(t.text).slice(0, ORG_TREE_MAX_TODO_TEXT)
              : "",
          done: !!t.done,
          assigneeMemberId:
            assignee == null || assignee === "" ? null : String(assignee),
        };
      })
    : [];
  const children = (root.children || []).map(normalizeTree);
  const title =
    root.title != null ? String(root.title).slice(0, ORG_TREE_MAX_TITLE) : "Untitled";
  const focus =
    root.focus != null ? String(root.focus).slice(0, ORG_TREE_MAX_FOCUS) : "";
  return {
    ...root,
    title,
    focus,
    people,
    managerMemberId,
    projects,
    todos,
    children,
  };
}

function newId() {
  return `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function defaultTree() {
  const ceo = {
    id: newId(),
    name: "Riya Sharma",
    role: "CEO",
    email: "riya.sharma@example.com",
    phoneNumber: "",
  };
  const engLead = {
    id: newId(),
    name: "Jordan Lee",
    role: "Engineering lead",
    email: "jordan.lee@example.com",
    phoneNumber: "",
  };
  const engIc = {
    id: newId(),
    name: "Sam Rivera",
    role: "Senior engineer",
    email: "sam.rivera@example.com",
    phoneNumber: "",
  };
  const platformLead = {
    id: newId(),
    name: "Alex Kim",
    role: "Platform lead",
    email: "alex.kim@example.com",
    phoneNumber: "",
  };
  const designLead = {
    id: newId(),
    name: "Morgan Chen",
    role: "Design lead",
    email: "morgan.chen@example.com",
    phoneNumber: "",
  };

  return normalizeTree({
    id: "root",
    title: "My company",
    kind: "org",
    focus: "Quarter goals across all teams",
    collapsed: false,
    people: [ceo],
    managerMemberId: ceo.id,
    projects: [
      { id: newId(), name: "FY roadmap", status: "active" },
      { id: newId(), name: "Board reporting pack", status: "backlog" },
    ],
    children: [
      {
        id: newId(),
        title: "Engineering",
        kind: "team",
        focus: "API reliability + mobile",
        collapsed: false,
        people: [engLead, engIc],
        managerMemberId: engLead.id,
        projects: [
          { id: newId(), name: "Mobile app v2", status: "active" },
          { id: newId(), name: "Core API hardening", status: "active" },
        ],
        children: [
          {
            id: newId(),
            title: "Platform",
            kind: "team",
            focus: "Infra hardening",
            collapsed: false,
            people: [platformLead],
            managerMemberId: platformLead.id,
            projects: [{ id: newId(), name: "K8s migration wave 1", status: "backlog" }],
            children: [
              {
                id: newId(),
                title: "Alex",
                kind: "person",
                focus: "On-call + deploy pipeline",
                collapsed: false,
                children: [],
                projects: [{ id: newId(), name: "Runbook refresh", status: "active" }],
                todos: [
                  { id: newId(), text: "Document runbooks", done: false },
                  { id: newId(), text: "Reduce flaky tests", done: true },
                ],
              },
            ],
            todos: [
              {
                id: newId(),
                text: "Cost review for staging",
                done: false,
                assigneeMemberId: "manager",
              },
            ],
          },
        ],
        todos: [],
      },
      {
        id: newId(),
        title: "Design",
        kind: "team",
        focus: "Design system refresh",
        collapsed: false,
        people: [designLead],
        managerMemberId: designLead.id,
        children: [],
        projects: [{ id: newId(), name: "Design system 2.0", status: "active" }],
        todos: [
          {
            id: newId(),
            text: "Audit components in Figma",
            done: false,
            assigneeMemberId: "manager",
          },
        ],
      },
    ],
    todos: [
      {
        id: newId(),
        text: "All-hands deck outline",
        done: false,
        assigneeMemberId: "manager",
      },
    ],
  });
}

function loadTree() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultTree();
    const parsed = JSON.parse(raw);
    if (parsed && parsed.id && Array.isArray(parsed.children))
      return normalizeTree(parsed);
  } catch {
    // ignore
  }
  return defaultTree();
}

function saveTree(tree) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tree));
  } catch {
    // ignore
  }
}

function findPathToId(root, targetId, path = []) {
  if (!root) return null;
  if (root.id === targetId) return [...path, root];
  for (const ch of root.children || []) {
    const hit = findPathToId(ch, targetId, [...path, root]);
    if (hit) return hit;
  }
  return null;
}

function isOrgLikeKind(kind) {
  return kind === "team" || kind === "org";
}

function defaultTodoAssigneeForVoice(node) {
  if (!node || !isOrgLikeKind(node.kind)) return null;
  if (node.managerMemberId) return "manager";
  const first = (node.people || [])[0];
  return first ? first.id : null;
}

function resolveAssigneeLabel(node, assigneeMemberId) {
  if (assigneeMemberId == null || assigneeMemberId === "") return "Unassigned";
  if (assigneeMemberId === "manager") {
    const pid = node.managerMemberId;
    const p = (node.people || []).find((x) => x.id === pid);
    return p ? `Manager (${p.name})` : "Manager";
  }
  const p = (node.people || []).find((x) => x.id === assigneeMemberId);
  return p ? p.name : "Unknown";
}

function cloneUpdateNode(root, nodeId, updater) {
  if (root.id === nodeId) return updater({ ...root });
  return {
    ...root,
    children: (root.children || []).map((c) => cloneUpdateNode(c, nodeId, updater)),
  };
}

function cloneRemoveChild(root, parentId, childId) {
  if (root.id === parentId) {
    return {
      ...root,
      children: (root.children || []).filter((c) => c.id !== childId),
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneRemoveChild(c, parentId, childId)),
  };
}

function cloneAddChild(root, parentId, child) {
  if (root.id === parentId) {
    return {
      ...root,
      children: [...(root.children || []), child],
      collapsed: false,
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneAddChild(c, parentId, child)),
  };
}

function cloneAddTodo(root, nodeId, todo) {
  if (root.id === nodeId) {
    return {
      ...root,
      todos: [...(root.todos || []), todo],
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneAddTodo(c, nodeId, todo)),
  };
}

function cloneToggleTodo(root, nodeId, todoId, done) {
  if (root.id === nodeId) {
    return {
      ...root,
      todos: (root.todos || []).map((t) =>
        t.id === todoId ? { ...t, done: !!done } : t
      ),
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneToggleTodo(c, nodeId, todoId, done)),
  };
}

function cloneDeleteTodo(root, nodeId, todoId) {
  if (root.id === nodeId) {
    return {
      ...root,
      todos: (root.todos || []).filter((t) => t.id !== todoId),
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneDeleteTodo(c, nodeId, todoId)),
  };
}

function cloneMapTodos(root, nodeId, mapper) {
  if (root.id === nodeId) {
    return {
      ...root,
      todos: (root.todos || []).map(mapper),
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneMapTodos(c, nodeId, mapper)),
  };
}

function cloneAddProject(root, nodeId, project) {
  if (root.id === nodeId) {
    return {
      ...root,
      projects: [...(root.projects || []), project],
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneAddProject(c, nodeId, project)),
  };
}

function cloneMapProjects(root, nodeId, mapper) {
  if (root.id === nodeId) {
    return {
      ...root,
      projects: (root.projects || []).map(mapper),
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneMapProjects(c, nodeId, mapper)),
  };
}

function cloneDeleteProject(root, nodeId, projectId) {
  if (root.id === nodeId) {
    return {
      ...root,
      projects: (root.projects || []).filter((p) => p.id !== projectId),
    };
  }
  return {
    ...root,
    children: (root.children || []).map((c) => cloneDeleteProject(c, nodeId, projectId)),
  };
}

function ordinalIndex(text) {
  const t = String(text || "").toLowerCase();
  const map = {
    first: 0,
    "1st": 0,
    one: 0,
    second: 1,
    "2nd": 1,
    two: 1,
    third: 2,
    "3rd": 2,
    three: 2,
    fourth: 3,
    "4th": 3,
    four: 3,
    fifth: 4,
    "5th": 4,
    five: 4,
  };
  for (const [word, idx] of Object.entries(map)) {
    if (new RegExp(`\\b${word}\\b`).test(t)) return idx;
  }
  const m = t.match(/\b(\d+)\b/);
  if (m) return Math.max(0, parseInt(m[1], 10) - 1);
  return null;
}

/**
 * Local voice navigation (browser STT only — no server / no AI Usage Credits).
 */
function parseVoiceCommand(raw, { selectedId, root, visibleChildrenTitles }) {
  const text = String(raw || "").trim();
  if (!text) return null;
  const t = text.toLowerCase();

  if (/^(go\s+)?back$|^parent$|^up$|^go\s+up$|^level\s+up$/.test(t)) {
    return { type: "parent" };
  }
  if (/^expand$|^open$|^show\s+children$|^open\s+children$/.test(t)) {
    return { type: "expand" };
  }
  if (/^collapse$|^close$|^hide\s+children$/.test(t)) {
    return { type: "collapse" };
  }
  if (/^read$|^summary$|^what'?s\s+here$|^describe$/.test(t)) {
    return { type: "read_summary" };
  }

  const sel = ordinalIndex(t);
  if (sel != null && /\bselect\b|\bpick\b|\bopen\b|\bgo\s+to\b/.test(t)) {
    return { type: "select_index", index: sel };
  }

  let m = t.match(/^(?:open|go to|select)\s+(.+)$/);
  if (m) {
    const name = m[1].trim();
    const pathToSel = findPathToId(root, selectedId);
    const current = pathToSel ? pathToSel[pathToSel.length - 1] : root;
    const pool = current.children || [];
    const hit = pool.find((n) => {
      const nt = n?.title != null ? String(n.title).toLowerCase() : "";
      const nm = name.toLowerCase();
      return nt && (nt.includes(nm) || nm.includes(nt));
    });
    if (hit) return { type: "select_id", id: hit.id };
  }

  m = t.match(/^(?:add\s+(?:a\s+)?)?(?:child|team|subteam|person|node)\s*[-:]?\s*(.+)$/i);
  if (m && m[1].trim()) {
    return { type: "add_child", title: m[1].trim() };
  }

  m = t.match(/^add\s+todo\s*[-:]?\s*(.+)$/i);
  if (m && m[1].trim()) {
    return { type: "add_todo", text: m[1].trim() };
  }

  m = t.match(/^add\s+project\s*[-:]?\s*(.+)$/i);
  if (m && m[1].trim()) {
    return { type: "add_project", name: m[1].trim() };
  }

  m = t.match(/^rename\s+to\s+(.+)$/i);
  if (m && m[1].trim()) {
    return { type: "rename", title: m[1].trim() };
  }

  m = t.match(/^set\s+focus\s+to\s+(.+)$/i);
  if (m && m[1].trim()) {
    return { type: "set_focus", text: m[1].trim() };
  }

  if (visibleChildrenTitles && visibleChildrenTitles.length) {
    const hitTitle = visibleChildrenTitles.find(
      (title) => title && t.includes(String(title).toLowerCase())
    );
    if (hitTitle) {
      const path = findPathToId(root, selectedId);
      const node = path ? path[path.length - 1] : root;
      const want = String(hitTitle || "").toLowerCase();
      const child = (node.children || []).find((c) => {
        const ct = c?.title != null ? String(c.title).toLowerCase() : "";
        return ct && ct === want;
      });
      if (child) return { type: "select_id", id: child.id };
    }
  }

  return null;
}

function PanelSection({ title, description, children, bg, border, descriptionColor }) {
  return (
    <Box
      borderWidth="1px"
      borderColor={border}
      borderRadius="2xl"
      bg={bg}
      px={{ base: 5, md: 7 }}
      py={{ base: 6, md: 8 }}
    >
      <Heading size="md" fontWeight="bold" letterSpacing="tight" mb={description ? 2 : 1}>
        {title}
      </Heading>
      {description ? (
        <Text
          fontSize="sm"
          color={descriptionColor}
          mb={5}
          lineHeight="tall"
          maxW="100%"
        >
          {description}
        </Text>
      ) : null}
      <Stack spacing={6}>{children}</Stack>
    </Box>
  );
}

const OrgTreeMap = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const toast = useToast();
  const [tree, setTree] = useState(() => loadTree());
  const [selectedId, setSelectedId] = useState("root");
  const [nodePanelOpen, setNodePanelOpen] = useState(true);

  const selectNode = useCallback((id) => {
    setSelectedId(id);
    setNodePanelOpen(true);
  }, []);
  const [childDraft, setChildDraft] = useState("");
  const [projectDraft, setProjectDraft] = useState("");
  const [todoDraft, setTodoDraft] = useState("");
  const [personNameDraft, setPersonNameDraft] = useState("");
  const [personRoleDraft, setPersonRoleDraft] = useState("");
  const [personEmailDraft, setPersonEmailDraft] = useState("");
  const [personPhoneDraft, setPersonPhoneDraft] = useState("");
  const [todoAssigneeDraft, setTodoAssigneeDraft] = useState("");
  const [myAssignments, setMyAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentFilter, setAssignmentFilter] = useState("open");
  const [voiceListening, setVoiceListening] = useState(false);
  const user = useSelector((s) => s.user || {});
  const importFileRef = useRef(null);
  const nodePanelRef = useRef(null);
  const skipFirstScrollRef = useRef(true);
  const remoteSaveTimerRef = useRef(null);
  const serverTreeLoadedRef = useRef(false);
  const prevSelectedIdForAssigneeRef = useRef(null);

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const tipBoxBg = useColorModeValue("gray.50", "gray.900");
  const tipStrong = useColorModeValue("gray.700", "gray.300");
  const sectionBg = useColorModeValue("gray.50", "gray.800");
  const sectionBorder = useColorModeValue("gray.200", "gray.600");
  const accordionHoverBg = useColorModeValue("gray.100", "gray.700");
  const stageRef = useRef(null);
  const [stageHeightPx, setStageHeightPx] = useState(560);
  const floatGlassBg = useColorModeValue(
    "rgba(255, 255, 255, 0.82)",
    "rgba(15, 23, 42, 0.88)"
  );
  const floatGlassBorder = useColorModeValue(
    "rgba(45, 212, 191, 0.5)",
    "rgba(45, 212, 191, 0.42)"
  );
  const floatShadow = useColorModeValue(
    "0 28px 60px -18px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(255,255,255,0.65) inset",
    "0 28px 70px -16px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255,255,255,0.06) inset"
  );
  const ambientBg = useColorModeValue(
    "linear-gradient(to bottom left, rgba(240, 253, 250, 0.92), transparent 40%)",
    "linear-gradient(to bottom left, rgba(17, 94, 89, 0.32), transparent 45%)"
  );
  const panelScrimBg = useColorModeValue("blackAlpha.200", "blackAlpha.500");
  const nodePanelCloseBg = useColorModeValue("teal.300", "teal.400");
  const nodePanelCloseHoverBg = useColorModeValue("teal.200", "teal.300");
  const canvasFallback = useBreakpointValue({ base: 420, md: 520, lg: 600 }) ?? 520;

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      requestAnimationFrame(() => {
        const h = el.getBoundingClientRect().height;
        if (h > 160) setStageHeightPx(Math.max(160, Math.floor(h)));
      });
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    saveTree(tree);
  }, [tree]);

  useEffect(() => {
    if (!getCookieToken()) return;
    if (serverTreeLoadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await hitBeApi("org-tree", "GET");
        if (cancelled) return;
        serverTreeLoadedRef.current = true;
        if (res?.success && res?.data?.tree) {
          setTree(normalizeTree(res.data.tree));
        }
      } catch {
        serverTreeLoadedRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** If selection points at a node that no longer exists (e.g. server tree id ≠ "root"), keep UI and mutations aligned. */
  useEffect(() => {
    if (!tree || typeof tree !== "object" || !tree.id) return;
    if (findPathToId(tree, selectedId)) return;
    setSelectedId(tree.id);
  }, [tree, selectedId]);

  const loadMyAssignments = useCallback(async () => {
    if (!getCookieToken()) return;
    setAssignmentsLoading(true);
    try {
      const res = await hitBeApi("org-tree/my-assignments", "GET");
      if (res?.success && Array.isArray(res?.data?.assignments)) {
        setMyAssignments(res.data.assignments);
      } else {
        setMyAssignments([]);
      }
    } catch {
      setMyAssignments([]);
    } finally {
      setAssignmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMyAssignments();
  }, [loadMyAssignments]);

  const patchMyAssignmentTodo = useCallback(
    async (row, done) => {
      const enc = encodeURIComponent;
      try {
        const path = `org-tree/assignments/${enc(row.ownerUserId)}/${enc(row.nodeId)}/${enc(row.todoId)}`;
        const res = await hitBeApi(path, "PATCH", { done: !!done });
        if (!res?.success) throw new Error(res?.message || "Could not update.");
        const uid = user?._id ?? user?.id;
        if (uid && String(row.ownerUserId) === String(uid)) {
          try {
            const tr = await hitBeApi("org-tree", "GET");
            if (tr?.success && tr?.data?.tree) setTree(normalizeTree(tr.data.tree));
          } catch {
            // ignore
          }
        }
        await loadMyAssignments();
      } catch (e) {
        toast({
          title: "Could not update task",
          description: e?.message || "Try again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [user, loadMyAssignments, toast]
  );

  useEffect(() => {
    if (!getCookieToken()) return;
    if (remoteSaveTimerRef.current) clearTimeout(remoteSaveTimerRef.current);
    remoteSaveTimerRef.current = setTimeout(() => {
      const payload = tree;
      hitBeApi("org-tree", "PUT", { tree: payload })
        .then((res) => {
          if (!res?.success) {
            throw new Error(res?.message || "Could not save org tree");
          }
        })
        .catch(() => {
          /* offline / guest — local copy still in localStorage */
        });
    }, 900);
    return () => {
      if (remoteSaveTimerRef.current) clearTimeout(remoteSaveTimerRef.current);
    };
  }, [tree]);

  useEffect(() => {
    setPersonNameDraft("");
    setPersonRoleDraft("");
    setPersonEmailDraft("");
    setPersonPhoneDraft("");
  }, [selectedId]);

  useEffect(() => {
    if (prevSelectedIdForAssigneeRef.current !== selectedId) {
      prevSelectedIdForAssigneeRef.current = selectedId;
      const p = findPathToId(tree, selectedId);
      const node = p ? p[p.length - 1] : null;
      if (!node || !isOrgLikeKind(node.kind)) setTodoAssigneeDraft("");
      else setTodoAssigneeDraft(defaultTodoAssigneeForVoice(node) || "");
    }
  }, [selectedId, tree]);

  useEffect(() => {
    if (skipFirstScrollRef.current) {
      skipFirstScrollRef.current = false;
      return;
    }
    const el = nodePanelRef.current;
    if (!el) return;
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [selectedId]);

  const path = useMemo(() => findPathToId(tree, selectedId) || [tree], [tree, selectedId]);
  const selected = path[path.length - 1];

  const visibleChildrenTitles = useMemo(() => {
    if (!selected) return [];
    return (selected.children || [])
      .filter((c) => !selected.collapsed)
      .map((c) => (c?.title != null ? String(c.title) : ""))
      .filter(Boolean);
  }, [selected]);

  const filteredAssignments = useMemo(() => {
    if (assignmentFilter === "open") return myAssignments.filter((a) => !a.done);
    if (assignmentFilter === "done") return myAssignments.filter((a) => a.done);
    return myAssignments;
  }, [myAssignments, assignmentFilter]);

  const applyVoice = useCallback(
    (rawText) => {
      const cmd = parseVoiceCommand(rawText, {
        selectedId,
        root: tree,
        visibleChildrenTitles,
      });
      if (!cmd) {
        toast({
          title: "Voice: no matching command",
          description:
            'Try: "select first", "go back", "expand", "add team Platform", "add project mobile", "add todo ship MVP", "set focus to …".',
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (cmd.type === "parent") {
        if (path.length >= 2) {
          selectNode(path[path.length - 2].id);
          speakBrowserAssistantReply("Moved up to parent.", {
            lang: "en-IN",
            flushDelayMs: 0,
          });
        } else {
          speakBrowserAssistantReply("Already at the top.", { lang: "en-IN", flushDelayMs: 0 });
        }
        return;
      }

      if (cmd.type === "expand") {
        setTree((t) =>
          cloneUpdateNode(t, selectedId, (n) => ({ ...n, collapsed: false }))
        );
        speakBrowserAssistantReply("Expanded.", { lang: "en-IN", flushDelayMs: 0 });
        return;
      }

      if (cmd.type === "collapse") {
        setTree((t) =>
          cloneUpdateNode(t, selectedId, (n) => ({ ...n, collapsed: true }))
        );
        speakBrowserAssistantReply("Collapsed.", { lang: "en-IN", flushDelayMs: 0 });
        return;
      }

      if (cmd.type === "select_id") {
        selectNode(cmd.id);
        const p = findPathToId(tree, cmd.id);
        const n = p ? p[p.length - 1] : null;
        const label = n?.title != null ? String(n.title) : "";
        speakBrowserAssistantReply(label ? `Selected ${label}.` : "Selected.", {
          lang: "en-IN",
          flushDelayMs: 0,
        });
        return;
      }

      if (cmd.type === "select_index") {
        const kids = selected.children || [];
        const node = kids[cmd.index];
        if (node) {
          selectNode(node.id);
          const label = node.title != null ? String(node.title) : "node";
          speakBrowserAssistantReply(`Selected ${label}.`, {
            lang: "en-IN",
            flushDelayMs: 0,
          });
        } else {
          speakBrowserAssistantReply("That child does not exist.", {
            lang: "en-IN",
            flushDelayMs: 0,
          });
        }
        return;
      }

      if (cmd.type === "add_child") {
        const child = emptyChildNode({
          title: cmd.title.slice(0, 200),
        });
        setTree((t) => cloneAddChild(t, selectedId, child));
        selectNode(child.id);
        speakBrowserAssistantReply(`Added ${child.title}.`, { lang: "en-IN", flushDelayMs: 0 });
        return;
      }

      if (cmd.type === "add_todo") {
        const assignee = defaultTodoAssigneeForVoice(selected);
        if (isOrgLikeKind(selected.kind) && !assignee) {
          toast({
            title: "Add teammates or a manager first",
            description: "Org and team todos must go to the manager or someone on the roster.",
            status: "warning",
            duration: 4500,
            isClosable: true,
          });
          speakBrowserAssistantReply(
            "Add people on this node or set a manager before adding a todo.",
            { lang: "en-IN", flushDelayMs: 0 }
          );
          return;
        }
        const todo = {
          id: newId(),
          text: cmd.text.slice(0, 500),
          done: false,
          assigneeMemberId: isOrgLikeKind(selected.kind) ? assignee : null,
        };
        setTree((t) => cloneAddTodo(t, selectedId, todo));
        speakBrowserAssistantReply("Todo added.", { lang: "en-IN", flushDelayMs: 0 });
        return;
      }

      if (cmd.type === "add_project") {
        const project = {
          id: newId(),
          name: cmd.name.slice(0, 200),
          status: "active",
        };
        setTree((t) => cloneAddProject(t, selectedId, project));
        speakBrowserAssistantReply(`Project ${project.name} added.`, {
          lang: "en-IN",
          flushDelayMs: 0,
        });
        return;
      }

      if (cmd.type === "rename") {
        setTree((t) =>
          cloneUpdateNode(t, selectedId, (n) => ({ ...n, title: cmd.title.slice(0, 200) }))
        );
        speakBrowserAssistantReply("Renamed.", { lang: "en-IN", flushDelayMs: 0 });
        return;
      }

      if (cmd.type === "set_focus") {
        setTree((t) =>
          cloneUpdateNode(t, selectedId, (n) => ({ ...n, focus: cmd.text.slice(0, 500) }))
        );
        speakBrowserAssistantReply("Focus updated.", { lang: "en-IN", flushDelayMs: 0 });
        return;
      }

      if (cmd.type === "read_summary") {
        const kids = (selected.children || [])
          .map((c) => (c?.title != null ? String(c.title) : ""))
          .filter(Boolean)
          .join(", ");
        const openTodos = (selected.todos || [])
          .filter((x) => !x.done)
          .map(
            (x) =>
              `${x.text} (${resolveAssigneeLabel(selected, x.assigneeMemberId)})`
          );
        const activeProjects = (selected.projects || [])
          .filter((p) => p.status !== "done")
          .map((p) => `${p.name} (${p.status})`);
        const parts = [];
        parts.push(selected.title);
        if (selected.focus) parts.push(`Focus: ${selected.focus}.`);
        if (isOrgLikeKind(selected.kind)) {
          const mgrPerson = (selected.people || []).find(
            (p) => p.id === selected.managerMemberId
          );
          if (mgrPerson) parts.push(`Manager: ${mgrPerson.name}.`);
          else parts.push("No manager set for this node.");
          const roster = (selected.people || []).map((p) => p.name).filter(Boolean);
          if (roster.length) parts.push(`People on this node: ${roster.join(", ")}.`);
          else parts.push("No people on this roster yet.");
        }
        if (kids) parts.push(`Teams or people under here: ${kids}.`);
        if (activeProjects.length)
          parts.push(`Active projects: ${activeProjects.join("; ")}.`);
        else parts.push("No active projects on this node.");
        if (openTodos.length)
          parts.push(`Open todos: ${openTodos.join("; ")}.`);
        else parts.push("No open todos on this node.");
        speakBrowserAssistantReply(parts.join(" "), { lang: "en-IN", flushDelayMs: 0 });
      }
    },
    [tree, selectedId, path, selected, visibleChildrenTitles, toast, selectNode]
  );

  const onMic = () => {
    if (!getSpeechRecognitionClass()) {
      toast({
        title: "Voice not supported",
        description: "Use Chrome or Edge for speech recognition.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    startBrowserVoiceDictation({
      lang: getPreferredSttLang(),
      onListeningStart: () => setVoiceListening(true),
      onListeningEnd: () => setVoiceListening(false),
      onFinal: (t) => {
        applyVoice(t);
      },
      onError: () => setVoiceListening(false),
    });
  };

  const onToggleCollapse = (id) => {
    setTree((t) =>
      cloneUpdateNode(t, id, (n) => ({ ...n, collapsed: !n.collapsed }))
    );
  };

  const addChildManual = () => {
    const title = childDraft.trim();
    if (!title) return;
    const child = emptyChildNode({ title: title.slice(0, 200) });
    setTree((t) => cloneAddChild(t, selectedId, child));
    setChildDraft("");
    selectNode(child.id);
  };

  const addProjectManual = () => {
    const name = projectDraft.trim();
    if (!name) return;
    const project = { id: newId(), name: name.slice(0, 200), status: "active" };
    setTree((t) => cloneAddProject(t, selectedId, project));
    setProjectDraft("");
  };

  const addPersonManual = () => {
    const name = personNameDraft.trim();
    if (!name) {
      toast({ title: "Enter a person name", status: "warning", duration: 2000 });
      return;
    }
    const emailRaw = personEmailDraft.trim();
    const phoneRaw = personPhoneDraft.trim();
    if (!emailRaw && !phoneRaw) {
      toast({
        title: "Add email or phone",
        description: "So this person can see assigned tasks when they log in with that account.",
        status: "warning",
        duration: 4500,
        isClosable: true,
      });
      return;
    }
    if (emailRaw && !looksLikeEmail(emailRaw)) {
      toast({ title: "Check the email format", status: "warning", duration: 3000 });
      return;
    }
    const em = normRosterEmail(emailRaw);
    const ph = normRosterPhone(phoneRaw);
    const dup = (selected.people || []).some((p) => {
      if (em && normRosterEmail(p.email) === em) return true;
      if (ph && rosterPhonesConflict(ph, p.phoneNumber)) return true;
      return false;
    });
    if (dup) {
      toast({
        title: "Duplicate contact",
        description: "That email or phone is already on this node’s roster.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const person = {
      id: newId(),
      name: name.slice(0, 120),
      role: personRoleDraft.trim().slice(0, 80),
      email: em,
      phoneNumber: ph,
    };
    setTree((t) =>
      cloneUpdateNode(t, selectedId, (n) => ({
        ...n,
        people: [...(n.people || []), person],
      }))
    );
    setPersonNameDraft("");
    setPersonRoleDraft("");
    setPersonEmailDraft("");
    setPersonPhoneDraft("");
  };

  const removePersonFromNode = (personId) => {
    setTree((t) =>
      cloneUpdateNode(t, selectedId, (n) => {
        const people = (n.people || []).filter((p) => p.id !== personId);
        let managerMemberId = n.managerMemberId;
        const wasManager = managerMemberId === personId;
        if (wasManager) managerMemberId = null;
        const todos = (n.todos || []).map((todo) => {
          if (todo.assigneeMemberId === personId)
            return { ...todo, assigneeMemberId: null };
          if (wasManager && todo.assigneeMemberId === "manager")
            return { ...todo, assigneeMemberId: null };
          return todo;
        });
        return { ...n, people, managerMemberId, todos };
      })
    );
  };

  const addTodoManual = () => {
    const text = todoDraft.trim();
    if (!text) return;
    let assigneeMemberId = null;
    if (isOrgLikeKind(selected.kind)) {
      if (!todoAssigneeDraft) {
        toast({
          title: "Choose an assignee",
          description: "Pick the manager or a teammate listed on this org/team.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      assigneeMemberId = todoAssigneeDraft === "manager" ? "manager" : todoAssigneeDraft;
      if (
        assigneeMemberId === "manager" &&
        !(selected.people || []).some((p) => p.id === selected.managerMemberId)
      ) {
        toast({
          title: "Set a manager first",
          description: "Choose a manager from the roster before assigning to manager.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      if (
        assigneeMemberId !== "manager" &&
        !(selected.people || []).some((p) => p.id === assigneeMemberId)
      ) {
        toast({ title: "Invalid assignee", status: "error", duration: 3000 });
        return;
      }
    }
    const todo = {
      id: newId(),
      text: text.slice(0, 500),
      done: false,
      assigneeMemberId,
    };
    setTree((t) => cloneAddTodo(t, selectedId, todo));
    setTodoDraft("");
  };

  const deleteSelected = () => {
    if (selectedId === "root") {
      toast({
        title: "Cannot delete root",
        description: "Rename the company node instead.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const parent = path[path.length - 2];
    if (!parent) return;
    setTree((t) => cloneRemoveChild(t, parent.id, selectedId));
    selectNode(parent.id);
  };

  const resetDemo = () => {
    const next = defaultTree();
    setTree(next);
    selectNode(next.id);
    toast({
      title: "Tree reset",
      description: "Sample org structure restored.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const speakHint = () => {
    speakBrowserAssistantReply(
      "Voice commands run in your browser only. Say select first, go back, expand, add team name, add project name, add todo text, or read summary. Use Export to download your tree JSON. Press Escape to close the detail panel.",
      { lang: "en-IN", flushDelayMs: 0 }
    );
  };

  const exportOrgTreeJson = useCallback(() => {
    try {
      const blob = new Blob([JSON.stringify(tree, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `org-tree-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Exported", description: "JSON file downloaded.", status: "success", duration: 2000 });
    } catch (e) {
      toast({
        title: "Export failed",
        description: String(e?.message || e),
        status: "error",
        duration: 3000,
      });
    }
  }, [tree, toast]);

  const onImportOrgTreeFile = useCallback(
    (e) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const raw = JSON.parse(String(reader.result || "{}"));
          if (
            !raw ||
            typeof raw !== "object" ||
            typeof raw.id !== "string" ||
            !Array.isArray(raw.children)
          ) {
            throw new Error("File must contain a tree object with id and children[].");
          }
          const next = normalizeTree(raw);
          setTree(next);
          selectNode(next.id);
          toast({
            title: "Tree imported",
            description: "Review the 3D view and detail panel. Signed-in users sync to the server automatically.",
            status: "success",
            duration: 4500,
            isClosable: true,
          });
        } catch (err) {
          toast({
            title: "Import failed",
            description: err?.message || "Invalid JSON.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      };
      reader.readAsText(f);
    },
    [selectNode, toast]
  );

  useEffect(() => {
    if (!nodePanelOpen) return;
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        setNodePanelOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nodePanelOpen]);

  const canvasH = Math.max(stageHeightPx, canvasFallback);

  return (
    <Box
      flex="1"
      display="flex"
      flexDirection="column"
      minH={0}
      h="100%"
      w="100%"
      bg={pageBg}
      position="relative"
      overflow="hidden"
    >
      <Flex
        flexShrink={0}
        w="100%"
        align={{ base: "stretch", md: "center" }}
        justify="space-between"
        gap={3}
        flexWrap="wrap"
        px={{ base: 3, md: 4 }}
        py={3}
        borderBottomWidth="1px"
        borderColor={border}
        bg={pageBg}
        zIndex={10}
      >
        <Box minW={0} maxW={{ base: "100%", md: "lg" }} pr={{ md: 2 }}>
          <Heading size="md" lineHeight="shorter">
            3D org tree
          </Heading>
          <Text fontSize="xs" color={muted} display={{ base: "none", sm: "block" }} mt={1}>
            Tap a sphere for details (✕ to hide). Roster people use email or phone so assignees see
            tasks here when they log in. Alt+click folds a branch.
          </Text>
        </Box>
        <HStack flexWrap="wrap" justify={{ base: "flex-start", md: "flex-end" }} spacing={2}>
          <input
            ref={importFileRef}
            type="file"
            accept="application/json,.json"
            style={{ display: "none" }}
            onChange={onImportOrgTreeFile}
          />
          <TabCacheButton tabId="org-tree" />
          <Tooltip label="Download the current tree as JSON (backup / share)">
            <Button size="sm" variant="outline" onClick={exportOrgTreeJson}>
              Export JSON
            </Button>
          </Tooltip>
          <Tooltip label="Replace the tree from a JSON file exported here or from a backup">
            <Button
              size="sm"
              variant="outline"
              onClick={() => importFileRef.current?.click()}
            >
              Import JSON
            </Button>
          </Tooltip>
          <Tooltip label="Browser-only speech; no AI Usage Credits">
            <Button
              leftIcon={<Icon as={FiMic} />}
              colorScheme="teal"
              variant="solid"
              size="sm"
              onClick={onMic}
              isLoading={voiceListening}
            >
              Voice navigate
            </Button>
          </Tooltip>
          <Button leftIcon={<Icon as={FiVolume2} />} size="sm" variant="outline" onClick={speakHint}>
            Hear tips
          </Button>
          <Button size="sm" variant="outline" colorScheme="orange" onClick={resetDemo}>
            Reset sample
          </Button>
        </HStack>
      </Flex>

      {getCookieToken() ? (
        <Box
          flexShrink={0}
          px={{ base: 3, md: 4 }}
          py={2}
          borderBottomWidth="1px"
          borderColor={border}
          bg={pageBg}
          zIndex={10}
        >
          <Flex align="center" justify="space-between" gap={3} mb={1} flexWrap="wrap">
            <Text fontWeight="semibold" fontSize="sm">
              Tasks assigned to you
            </Text>
            <HStack spacing={1}>
              <Button
                size="xs"
                variant={assignmentFilter === "open" ? "solid" : "outline"}
                colorScheme="teal"
                onClick={() => setAssignmentFilter("open")}
              >
                Open
              </Button>
              <Button
                size="xs"
                variant={assignmentFilter === "done" ? "solid" : "outline"}
                colorScheme="teal"
                onClick={() => setAssignmentFilter("done")}
              >
                Done
              </Button>
              <Button
                size="xs"
                variant={assignmentFilter === "all" ? "solid" : "outline"}
                colorScheme="teal"
                onClick={() => setAssignmentFilter("all")}
              >
                All
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => loadMyAssignments()}
                isLoading={assignmentsLoading}
              >
                Refresh
              </Button>
            </HStack>
          </Flex>
          <Text fontSize="xs" color={muted} mb={2}>
            Matches your profile email or phone to roster entries on any org tree. Complete your
            profile if this list is empty.
          </Text>
          {assignmentsLoading && !myAssignments.length ? (
            <Spinner size="sm" color="teal.400" />
          ) : myAssignments.length === 0 ? (
            <Text fontSize="sm" color={muted}>
              No matching assignments yet.
            </Text>
          ) : filteredAssignments.length === 0 ? (
            <Text fontSize="sm" color={muted}>
              No tasks in this filter.
            </Text>
          ) : (
            <Stack spacing={2} maxH="min(28dvh, 220px)" overflowY="auto">
              {filteredAssignments.map((a) => (
                <Flex
                  key={`${a.ownerUserId}-${a.nodeId}-${a.todoId}`}
                  align="flex-start"
                  gap={3}
                  p={3}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={border}
                  bg={cardBg}
                  flexWrap="wrap"
                >
                  <Checkbox
                    isChecked={!!a.done}
                    onChange={(e) => patchMyAssignmentTodo(a, e.target.checked)}
                    mt={1}
                  />
                  <Box flex="1" minW={0}>
                    <Text fontWeight="medium" wordBreak="break-word">
                      {a.text}
                    </Text>
                    <Text fontSize="xs" color={muted} mt={1}>
                      {(a.companyTitle || "Org") +
                        (a.pathTitles && a.pathTitles.length
                          ? ` → ${a.pathTitles.filter(Boolean).join(" → ")}`
                          : "")}
                    </Text>
                    <Text fontSize="xs" color={muted}>
                      Owner: {a.ownerName || "—"}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Stack>
          )}
        </Box>
      ) : null}

      <Box
        ref={stageRef}
        flex="1"
        minH={0}
        w="100%"
        position="relative"
        isolation="isolate"
        bg={cardBg}
      >
        <Box
          pointerEvents="none"
          position="absolute"
          inset={0}
          zIndex={1}
          sx={{ background: ambientBg }}
        />
        <Box
          position="absolute"
          inset={0}
          zIndex={2}
          sx={{ pointerEvents: "auto", touchAction: "none" }}
        >
          <Suspense
            fallback={
              <Center h="100%" minH="240px" bg={tipBoxBg}>
                <Spinner color="teal.400" size="xl" thickness="4px" />
              </Center>
            }
          >
            <OrgTree3DScene
              tree={tree}
              selectedId={selectedId}
              onSelectNode={selectNode}
              onToggleCollapse={onToggleCollapse}
              isDark={isDark}
              height={canvasH}
            />
          </Suspense>
        </Box>

        {/*
          Centered sheet: overlay is pointer-events none so the 3D canvas still receives drags/clicks
          in the margins; only the glass card captures events.
        */}
        {nodePanelOpen ? (
        <Box
          position="absolute"
          inset={0}
          zIndex={80}
          pointerEvents="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={{ base: 3, sm: 4, md: 6 }}
        >
          <Box
            position="absolute"
            inset={0}
            pointerEvents="none"
            bg={panelScrimBg}
            sx={{ backdropFilter: "blur(2px)" }}
          />
          <MotionBox
            ref={nodePanelRef}
            id="org-tree-node-panel"
            key={selectedId}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
            pointerEvents="auto"
            position="relative"
            zIndex={1}
            flexShrink={0}
            w="100%"
            maxW={{ base: "100%", md: "min(1120px, 100%)" }}
            maxH="min(90dvh, 100%)"
            minW={0}
            overflowY="auto"
            overflowX="hidden"
            borderRadius="3xl"
            borderWidth="1px"
            borderColor={floatGlassBorder}
            bg={floatGlassBg}
            boxShadow={floatShadow}
            sx={{
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              overscrollBehavior: "contain",
            }}
          >
            <Flex align="stretch" gap={0} minH="min-content">
              <Box
                aria-hidden
                flexShrink={0}
                w={{ base: "6px", md: "8px" }}
                borderRadius="full"
                bgGradient="linear(to-b, teal.400, cyan.300)"
                boxShadow="0 0 18px rgba(45,212,191,0.45)"
                my={{ base: 5, md: 6 }}
                ml={{ base: 3, md: 4 }}
              />
              <Box
                flex="1"
                minW={0}
                px={{ base: 3, md: 5 }}
                pr={{ base: 3, md: 5 }}
                py={{ base: 5, md: 7 }}
              >
                <VStack align="stretch" spacing={{ base: 6, md: 8 }}>
              <Box pb={6} mb={1} borderBottomWidth="1px" borderColor={border}>
                <Flex
                  align={{ base: "stretch", sm: "flex-start" }}
                  justify="space-between"
                  gap={4}
                  direction={{ base: "column", sm: "row" }}
                  mb={4}
                >
                  <Box minW={0} flex="1">
                    {path.length > 1 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="teal"
                        leftIcon={<ChevronLeftIcon />}
                        mb={2}
                        px={0}
                        h="auto"
                        py={1}
                        fontWeight="medium"
                        onClick={() => selectNode(path[path.length - 2].id)}
                      >
                        Back to “{path[path.length - 2].title}”
                      </Button>
                    ) : null}
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color="teal.500"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      mb={2}
                    >
                      This node
                    </Text>
                    <HStack spacing={2} mb={2} flexWrap="wrap">
                      <Badge
                        fontSize="0.75rem"
                        px={2}
                        py={0.5}
                        colorScheme={(KINDS[selected.kind] || KINDS.team).colorScheme}
                      >
                        {(KINDS[selected.kind] || KINDS.team).label}
                      </Badge>
                    </HStack>
                    {path.length > 1 ? (
                      <Text
                        fontSize="xs"
                        color={muted}
                        mb={2}
                        lineHeight="short"
                        wordBreak="break-word"
                      >
                        Path:{" "}
                        {path
                          .slice(0, -1)
                          .map((p) => p.title)
                          .join(" → ")}
                      </Text>
                    ) : null}
                    <Heading size="xl" lineHeight="short" wordBreak="break-word">
                      {selected.title}
                    </Heading>
                    <Text fontSize="sm" color={muted} mt={2} maxW="lg">
                      Use the tabs: set the basics, add people or a new branch in the tree, then
                      projects and tasks.
                    </Text>
                  </Box>
                  <HStack
                    spacing={3}
                    flexShrink={0}
                    alignSelf={{ base: "stretch", sm: "flex-start" }}
                    justify={{ base: "flex-end", sm: "flex-start" }}
                  >
                    {selectedId !== "root" ? (
                      <Button
                        size="md"
                        colorScheme="red"
                        variant="outline"
                        leftIcon={<DeleteIcon />}
                        onClick={deleteSelected}
                      >
                        Delete node
                      </Button>
                    ) : null}
                    <Tooltip label="Hide panel (selection stays on the tree)" placement="bottom" hasArrow>
                      <IconButton
                        aria-label="Close details panel"
                        icon={<CloseIcon />}
                        variant="unstyled"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxSize="40px"
                        minW="40px"
                        borderRadius="lg"
                        bg={nodePanelCloseBg}
                        color="gray.900"
                        boxShadow="sm"
                        _hover={{ bg: nodePanelCloseHoverBg }}
                        _active={{ bg: nodePanelCloseHoverBg, transform: "scale(0.97)" }}
                        _focusVisible={{
                          boxShadow: "0 0 0 3px rgba(45, 212, 191, 0.55)",
                        }}
                        onClick={() => setNodePanelOpen(false)}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
              </Box>

              <Tabs colorScheme="teal" variant="enclosed" isLazy>
                <TabList flexWrap="wrap" gap={1}>
                  <Tab>About</Tab>
                  <Tab>People & branches</Tab>
                  <Tab>Projects & tasks</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={{ base: 0, md: 1 }} pt={4}>
                    <PanelSection
                      title="Basics"
                      description="Name, type, current focus, and collapsing the 3D subtree."
                      bg={sectionBg}
                      border={sectionBorder}
                      descriptionColor={muted}
                    >
                      <FormControl>
                        <FormLabel fontWeight="semibold">Title</FormLabel>
                        <Input
                          size="md"
                          value={selected.title}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTree((t) =>
                              cloneUpdateNode(t, selectedId, (n) => ({ ...n, title: v }))
                            );
                          }}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel fontWeight="semibold">Kind</FormLabel>
                        <Wrap spacing={2}>
                          {Object.keys(KINDS).map((k) => (
                            <WrapItem key={k}>
                              <Button
                                size="md"
                                variant={selected.kind === k ? "solid" : "outline"}
                                colorScheme={(KINDS[k] || {}).colorScheme || "gray"}
                                onClick={() =>
                                  setTree((t) =>
                                    cloneUpdateNode(t, selectedId, (n) => ({ ...n, kind: k }))
                                  )
                                }
                              >
                                {KINDS[k].label}
                              </Button>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </FormControl>

                      {(selected.children || []).length > 0 ? (
                        <Flex
                          align="center"
                          justify="space-between"
                          gap={4}
                          wrap="wrap"
                          py={1}
                        >
                          <FormLabel fontWeight="semibold" mb={0}>
                            Collapse subtree in 3D
                          </FormLabel>
                          <Switch
                            isChecked={!!selected.collapsed}
                            onChange={(e) =>
                              setTree((t) =>
                                cloneUpdateNode(t, selectedId, (n) => ({
                                  ...n,
                                  collapsed: e.target.checked,
                                }))
                              )
                            }
                            colorScheme="teal"
                            size="lg"
                          />
                        </Flex>
                      ) : null}

                      <FormControl>
                        <FormLabel fontWeight="semibold">Focus (optional)</FormLabel>
                        <Textarea
                          size="md"
                          rows={5}
                          value={selected.focus || ""}
                          placeholder="e.g. Q2 release, hiring, onboarding…"
                          onChange={(e) =>
                            setTree((t) =>
                              cloneUpdateNode(t, selectedId, (n) => ({
                                ...n,
                                focus: e.target.value,
                              }))
                            )
                          }
                        />
                      </FormControl>
                    </PanelSection>
                  </TabPanel>

                  <TabPanel px={{ base: 0, md: 1 }} pt={4}>
                    <VStack align="stretch" spacing={{ base: 6, md: 8 }}>
                      {isOrgLikeKind(selected.kind) ? (
                        <PanelSection
                          title="People on this node"
                          description="Use the same email or phone they use for Manage360. Pick a manager, then you can assign tasks to people here."
                          bg={sectionBg}
                          border={sectionBorder}
                          descriptionColor={muted}
                        >
                          <FormControl>
                            <FormLabel fontWeight="semibold">Manager</FormLabel>
                            <Select
                              size="md"
                              placeholder="No manager"
                              value={selected.managerMemberId || ""}
                              onChange={(e) => {
                                const v = e.target.value || null;
                                setTree((t) =>
                                  cloneUpdateNode(t, selectedId, (n) => ({
                                    ...n,
                                    managerMemberId: v,
                                  }))
                                );
                              }}
                            >
                              <option value="">No manager</option>
                              {(selected.people || []).map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                  {p.role ? ` — ${p.role}` : ""}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl>
                            <FormLabel fontWeight="semibold">Add someone</FormLabel>
                            <Text fontSize="xs" color={muted} mb={2}>
                              Name + email or phone (digits only is fine).
                            </Text>
                            <Stack spacing={2}>
                              <Input
                                size="md"
                                placeholder="Name"
                                value={personNameDraft}
                                onChange={(e) => setPersonNameDraft(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addPersonManual()}
                              />
                              <HStack spacing={2} flexWrap="wrap" align="flex-start">
                                <Input
                                  size="md"
                                  flex="1"
                                  minW="160px"
                                  type="email"
                                  autoComplete="email"
                                  placeholder="Email"
                                  value={personEmailDraft}
                                  onChange={(e) => setPersonEmailDraft(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && addPersonManual()}
                                />
                                <Input
                                  size="md"
                                  flex="1"
                                  minW="140px"
                                  type="tel"
                                  autoComplete="tel"
                                  placeholder="Phone"
                                  value={personPhoneDraft}
                                  onChange={(e) => setPersonPhoneDraft(e.target.value)}
                                  onKeyDown={(e) => e.key === "Enter" && addPersonManual()}
                                />
                              </HStack>
                              <Input
                                size="md"
                                placeholder="Role (optional)"
                                value={personRoleDraft}
                                onChange={(e) => setPersonRoleDraft(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && addPersonManual()}
                              />
                              <Button
                                colorScheme="teal"
                                size="md"
                                alignSelf="flex-start"
                                onClick={addPersonManual}
                                leftIcon={<AddIcon />}
                              >
                                Add to roster
                              </Button>
                            </Stack>
                          </FormControl>

                          {(selected.people || []).length === 0 ? (
                            <Text fontSize="sm" color={muted}>
                              No one listed yet — add teammates to assign work.
                            </Text>
                          ) : (
                            <Stack spacing={2}>
                              {(selected.people || []).map((p) => (
                                <Flex
                                  key={p.id}
                                  align="center"
                                  justify="space-between"
                                  gap={3}
                                  p={3}
                                  borderWidth="1px"
                                  borderColor={border}
                                  borderRadius="lg"
                                  bg={cardBg}
                                  flexWrap="wrap"
                                >
                                  <Box minW={0}>
                                    <Text fontWeight="semibold" wordBreak="break-word">
                                      {p.name}
                                    </Text>
                                    {p.role ? (
                                      <Text fontSize="sm" color={muted}>
                                        {p.role}
                                      </Text>
                                    ) : null}
                                    {p.email ? (
                                      <Text fontSize="xs" color={muted} wordBreak="break-all">
                                        {p.email}
                                      </Text>
                                    ) : null}
                                    {p.phoneNumber ? (
                                      <Text fontSize="xs" color={muted}>
                                        {p.phoneNumber}
                                      </Text>
                                    ) : null}
                                  </Box>
                                  <HStack spacing={2}>
                                    {selected.managerMemberId === p.id ? (
                                      <Badge colorScheme="teal">Manager</Badge>
                                    ) : null}
                                    <IconButton
                                      aria-label={`Remove ${p.name}`}
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      variant="ghost"
                                      colorScheme="red"
                                      onClick={() => removePersonFromNode(p.id)}
                                    />
                                  </HStack>
                                </Flex>
                              ))}
                            </Stack>
                          )}
                        </PanelSection>
                      ) : (
                        <Box
                          borderWidth="1px"
                          borderColor={sectionBorder}
                          borderRadius="2xl"
                          bg={sectionBg}
                          px={{ base: 5, md: 6 }}
                          py={5}
                        >
                          <Text fontSize="sm" color={muted}>
                            <Text as="span" fontWeight="semibold" color={tipStrong}>
                              Person node:
                            </Text>{" "}
                            no roster here. Add a branch below if this person should have teams
                            under them in the tree.
                          </Text>
                        </Box>
                      )}

                      <PanelSection
                        title="New branch in the tree"
                        description="Creates another sphere under this one in the 3D view (team, sub-team, or person)."
                        bg={sectionBg}
                        border={sectionBorder}
                        descriptionColor={muted}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Input
                            size="md"
                            placeholder="Name for the new branch"
                            value={childDraft}
                            onChange={(e) => setChildDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addChildManual()}
                          />
                          <Button
                            colorScheme="teal"
                            size="md"
                            alignSelf="flex-start"
                            onClick={addChildManual}
                            leftIcon={<AddIcon />}
                          >
                            Add branch
                          </Button>
                        </VStack>
                      </PanelSection>
                    </VStack>
                  </TabPanel>

                  <TabPanel px={{ base: 0, md: 1 }} pt={4}>
                    <VStack align="stretch" spacing={{ base: 6, md: 8 }}>
                      <PanelSection
                        title="Projects"
                        description="Larger pieces of work (backlog → active → done)."
                        bg={sectionBg}
                        border={sectionBorder}
                        descriptionColor={muted}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Input
                            size="md"
                            placeholder="New project name"
                            value={projectDraft}
                            onChange={(e) => setProjectDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addProjectManual()}
                          />
                          <Button
                            colorScheme="purple"
                            size="md"
                            alignSelf="flex-start"
                            onClick={addProjectManual}
                            leftIcon={<AddIcon />}
                          >
                            Add project
                          </Button>
                        </VStack>
                        <Stack spacing={3} mt={2}>
                          {(selected.projects || []).length === 0 ? (
                            <Text fontSize="sm" color={muted} py={2}>
                              No projects yet.
                            </Text>
                          ) : (
                            (selected.projects || []).map((proj) => (
                              <Stack
                                key={proj.id}
                                direction={{ base: "column", md: "row" }}
                                spacing={3}
                                align={{ base: "stretch", md: "center" }}
                                p={4}
                                borderWidth="1px"
                                borderColor={border}
                                borderRadius="lg"
                                bg={cardBg}
                              >
                                <Input
                                  size="md"
                                  flex="1"
                                  minW={0}
                                  value={proj.name}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setTree((t) =>
                                      cloneMapProjects(t, selectedId, (p) =>
                                        p.id === proj.id ? { ...p, name: v } : p
                                      )
                                    );
                                  }}
                                />
                                <HStack spacing={3} flexShrink={0} w={{ base: "full", md: "auto" }}>
                                  <Select
                                    size="md"
                                    minW={{ md: "160px" }}
                                    flex="1"
                                    value={proj.status}
                                    onChange={(e) =>
                                      setTree((t) =>
                                        cloneMapProjects(t, selectedId, (p) =>
                                          p.id === proj.id ? { ...p, status: e.target.value } : p
                                        )
                                      )
                                    }
                                  >
                                    {PROJECT_STATUS_OPTIONS.map((o) => (
                                      <option key={o.value} value={o.value}>
                                        {o.label}
                                      </option>
                                    ))}
                                  </Select>
                                  <IconButton
                                    aria-label="Remove project"
                                    icon={<DeleteIcon />}
                                    size="md"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() =>
                                      setTree((t) => cloneDeleteProject(t, selectedId, proj.id))
                                    }
                                  />
                                </HStack>
                              </Stack>
                            ))
                          )}
                        </Stack>
                      </PanelSection>

                      <PanelSection
                        title="Tasks"
                        description={
                          isOrgLikeKind(selected.kind)
                            ? "Checklist items — assign to the manager or someone on the roster (People tab)."
                            : "Simple checklist for this person."
                        }
                        bg={sectionBg}
                        border={sectionBorder}
                        descriptionColor={muted}
                      >
                        <VStack align="stretch" spacing={3}>
                          {isOrgLikeKind(selected.kind) ? (
                            <FormControl>
                              <FormLabel fontWeight="semibold">Assign new task to</FormLabel>
                              <Select
                                size="md"
                                placeholder="Manager or teammate…"
                                value={todoAssigneeDraft || ""}
                                onChange={(e) => setTodoAssigneeDraft(e.target.value)}
                              >
                                {selected.managerMemberId ? (
                                  <option value="manager">
                                    Manager (
                                    {(selected.people || []).find(
                                      (p) => p.id === selected.managerMemberId
                                    )?.name || "?"}
                                    )
                                  </option>
                                ) : null}
                                {(selected.people || []).map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name}
                                    {p.role ? ` — ${p.role}` : ""}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          ) : null}
                          <Input
                            size="md"
                            placeholder="What needs to be done?"
                            value={todoDraft}
                            onChange={(e) => setTodoDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addTodoManual()}
                          />
                          <Button
                            colorScheme="teal"
                            variant="outline"
                            size="md"
                            alignSelf="flex-start"
                            onClick={addTodoManual}
                            leftIcon={<AddIcon />}
                          >
                            Add task
                          </Button>
                        </VStack>
                        <Stack spacing={3} mt={2}>
                          {(selected.todos || []).length === 0 ? (
                            <Text fontSize="sm" color={muted} py={2}>
                              No tasks yet.
                            </Text>
                          ) : (
                            (selected.todos || []).map((td) => (
                              <Flex
                                key={td.id}
                                p={4}
                                borderWidth="1px"
                                borderColor={border}
                                borderRadius="lg"
                                bg={cardBg}
                                align="flex-start"
                                gap={4}
                                flexWrap="wrap"
                              >
                                <Box flex="1" minW={0}>
                                  <Checkbox
                                    isChecked={td.done}
                                    onChange={(e) =>
                                      setTree((t) =>
                                        cloneToggleTodo(t, selectedId, td.id, e.target.checked)
                                      )
                                    }
                                  >
                                    <Text
                                      as="span"
                                      fontSize="md"
                                      textDecoration={td.done ? "line-through" : "none"}
                                      color={td.done ? muted : "inherit"}
                                      wordBreak="break-word"
                                    >
                                      {td.text}
                                    </Text>
                                  </Checkbox>
                                  {isOrgLikeKind(selected.kind) ? (
                                    <Badge mt={2} colorScheme="cyan" variant="subtle">
                                      {resolveAssigneeLabel(selected, td.assigneeMemberId)}
                                    </Badge>
                                  ) : null}
                                </Box>
                                {isOrgLikeKind(selected.kind) ? (
                                  <Select
                                    size="sm"
                                    maxW="220px"
                                    flexShrink={0}
                                    value={td.assigneeMemberId || ""}
                                    onChange={(e) => {
                                      const v = e.target.value || null;
                                      setTree((t) =>
                                        cloneMapTodos(t, selectedId, (x) =>
                                          x.id === td.id
                                            ? { ...x, assigneeMemberId: v }
                                            : x
                                        )
                                      );
                                    }}
                                  >
                                    <option value="">Unassigned</option>
                                    {selected.managerMemberId ? (
                                      <option value="manager">Manager</option>
                                    ) : null}
                                    {(selected.people || []).map((p) => (
                                      <option key={p.id} value={p.id}>
                                        {p.name}
                                      </option>
                                    ))}
                                  </Select>
                                ) : null}
                                <IconButton
                                  aria-label="Remove todo"
                                  icon={<DeleteIcon />}
                                  size="md"
                                  variant="ghost"
                                  colorScheme="red"
                                  flexShrink={0}
                                  alignSelf="flex-start"
                                  onClick={() =>
                                    setTree((t) => cloneDeleteTodo(t, selectedId, td.id))
                                  }
                                />
                              </Flex>
                            ))
                          )}
                        </Stack>
                      </PanelSection>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>

              <Accordion allowToggle reduceMotion defaultIndex={[]}>
                <AccordionItem border="none">
                  <h2>
                    <AccordionButton
                      borderRadius="lg"
                      px={4}
                      py={3}
                      bg={tipBoxBg}
                      _hover={{ bg: accordionHoverBg }}
                    >
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="semibold" fontSize="sm">
                          Voice tips (optional)
                        </Text>
                        <Text fontSize="xs" color={muted} fontWeight="normal">
                          Expand for spoken-command examples
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel px={0} pt={3} pb={1}>
                    <Box
                      p={4}
                      borderRadius="lg"
                      bg={tipBoxBg}
                      fontSize="sm"
                      color={muted}
                      lineHeight="tall"
                    >
                      <Text color={tipStrong} fontWeight="semibold" mb={2}>
                        Examples
                      </Text>
                      <Text>
                        <strong>select first</strong> / <strong>select second</strong> ·{" "}
                        <strong>go back</strong> · <strong>expand</strong> / <strong>collapse</strong>{" "}
                        (updates the 3D branch) · <strong>open engineering</strong> ·{" "}
                        <strong>add team platform</strong> ·{" "}
                        <strong>add project mobile redesign</strong> ·{" "}
                        <strong>add todo ship dashboard</strong> · <strong>rename to …</strong> ·{" "}
                        <strong>set focus to …</strong> · <strong>read summary</strong>
                      </Text>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
                </VStack>
              </Box>
            </Flex>
          </MotionBox>
        </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default OrgTreeMap;
